import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Tabs as TabsPrimitive } from 'radix-ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { cn } from '@/lib/utils';

function Tabs({
  className,
  orientation = 'horizontal',
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      data-orientation={orientation}
      orientation={orientation}
      className={cn('group/tabs flex gap-2 data-[orientation=horizontal]:flex-col', className)}
      {...props}
    />
  );
}

const tabsListVariants = cva(
  'group/tabs-list inline-flex w-fit max-w-full items-center justify-center overflow-x-auto overflow-y-hidden rounded-lg p-[3px] text-muted-foreground [scrollbar-width:none] group-data-[orientation=horizontal]/tabs:h-9 group-data-[orientation=vertical]/tabs:h-fit group-data-[orientation=vertical]/tabs:flex-col data-[variant=line]:rounded-none [&::-webkit-scrollbar]:hidden',
  {
    variants: {
      variant: {
        default: 'bg-muted',
        line: 'justify-start gap-1 bg-transparent',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

function TabsList({
  className,
  variant = 'default',
  onScroll,
  children,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> & VariantProps<typeof tabsListVariants>) {
  const listRef = React.useRef<React.ElementRef<typeof TabsPrimitive.List>>(null);
  const [overflow, setOverflow] = React.useState({ left: false, right: false });

  const updateOverflow = React.useCallback(() => {
    const element = listRef.current;
    if (!element) return;

    const maxScrollLeft = element.scrollWidth - element.clientWidth;
    const left = maxScrollLeft > 1 && element.scrollLeft > 1;
    const right = maxScrollLeft > 1 && element.scrollLeft < maxScrollLeft - 1;

    setOverflow((current) =>
      current.left === left && current.right === right ? current : { left, right },
    );
  }, []);

  React.useEffect(() => {
    updateOverflow();
    const element = listRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(updateOverflow);
    observer.observe(element);
    return () => observer.disconnect();
  }, [children, updateOverflow]);

  const overflowMaskClass =
    overflow.left && overflow.right
      ? '[mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_24px,black_calc(100%-24px),transparent)]'
      : overflow.left
        ? '[mask-image:linear-gradient(to_right,transparent,black_24px,black_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent,black_24px,black_100%)]'
        : overflow.right
          ? '[mask-image:linear-gradient(to_right,black_0,black_calc(100%-24px),transparent)] [-webkit-mask-image:linear-gradient(to_right,black_0,black_calc(100%-24px),transparent)]'
          : '';

  function scrollTabs(direction: 'left' | 'right') {
    const element = listRef.current;
    if (!element) return;
    const distance = Math.max(120, Math.floor(element.clientWidth * 0.7));
    element.scrollBy({
      left: direction === 'left' ? -distance : distance,
      behavior: 'smooth',
    });
  }

  return (
    <div className="relative max-w-full">
      <TabsPrimitive.List
        ref={listRef}
        data-slot="tabs-list"
        data-variant={variant}
        data-overflow-left={overflow.left}
        data-overflow-right={overflow.right}
        className={cn(
          tabsListVariants({ variant }),
          overflowMaskClass,
          className,
          variant === 'line' && 'border-b-0',
        )}
        onScroll={(event) => {
          updateOverflow();
          onScroll?.(event);
        }}
        {...props}
      >
        {children}
      </TabsPrimitive.List>
      {overflow.left && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          className="absolute left-0 top-1/2 z-10 flex size-5 -translate-y-1/2 items-center justify-center rounded-full border bg-background/95 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => scrollTabs('left')}
        >
          <ChevronLeft className="size-3" />
        </button>
      )}
      {overflow.right && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          className="absolute right-0 top-1/2 z-10 flex size-5 -translate-y-1/2 items-center justify-center rounded-full border bg-background/95 text-muted-foreground shadow-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => scrollTabs('right')}
        >
          <ChevronRight className="size-3" />
        </button>
      )}
    </div>
  );
}

function TabsTrigger({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "relative inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-sm font-medium whitespace-nowrap text-foreground/60 transition-all group-data-[orientation=vertical]/tabs:w-full group-data-[orientation=vertical]/tabs:justify-start hover:text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-1 focus-visible:outline-ring disabled:pointer-events-none disabled:opacity-50 group-data-[variant=default]/tabs-list:data-[state=active]:shadow-sm group-data-[variant=line]/tabs-list:data-[state=active]:shadow-none dark:text-muted-foreground dark:hover:text-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        'group-data-[variant=line]/tabs-list:rounded-none group-data-[variant=line]/tabs-list:border-0 group-data-[variant=line]/tabs-list:bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:!border-transparent group-data-[variant=line]/tabs-list:data-[state=active]:!bg-transparent group-data-[variant=line]/tabs-list:data-[state=active]:font-medium group-data-[variant=line]/tabs-list:data-[state=active]:text-foreground dark:group-data-[variant=line]/tabs-list:data-[state=active]:!border-transparent dark:group-data-[variant=line]/tabs-list:data-[state=active]:!bg-transparent',
        'data-[state=active]:bg-background data-[state=active]:text-foreground group-data-[variant=line]/tabs-list:data-[state=active]:!bg-transparent dark:data-[state=active]:border-input dark:data-[state=active]:bg-input/30 dark:data-[state=active]:text-foreground dark:group-data-[variant=line]/tabs-list:data-[state=active]:!bg-transparent',
        'after:absolute after:bg-foreground after:opacity-0 after:transition-opacity group-data-[orientation=horizontal]/tabs:after:inset-x-0 group-data-[orientation=horizontal]/tabs:after:bottom-[-5px] group-data-[orientation=horizontal]/tabs:after:h-0.5 group-data-[orientation=vertical]/tabs:after:inset-y-0 group-data-[orientation=vertical]/tabs:after:-right-1 group-data-[orientation=vertical]/tabs:after:w-0.5 group-data-[variant=default]/tabs-list:data-[state=active]:after:opacity-100 group-data-[variant=line]/tabs-list:data-[state=active]:after:opacity-100',
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn('flex-1 outline-none', className)}
      {...props}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Tabs, TabsList, TabsTrigger, TabsContent, tabsListVariants };
